import { ChangeEventHandler, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { SideMenu, Header, SideMenuViews, Toolbox, ToolboxViews, Modal } from '@/components';
import { useAreaConfig, useDeviceType, useElementSize, useSubjectConfig } from '@/hooks';
import { SubjectViewState } from '../ThreeDeeViewer/Experience/Subject';
import { Category, LabelConfig, Unit, ViewerType } from '#server/models';
import { NavBarMenuMobile } from '../Header/NavBarMenuMobile';
import { DXPAnalytics } from '@curtin-dxp/web-client';
import { getCommonAnalyticsContext } from '@/lib/utils';
import { LayoutContext } from '@/contexts';

export interface SideMenuState {
  open: boolean;
  view: SideMenuViews;
}

export interface ToolboxState {
  open: boolean;
  view: ToolboxViews;
}

export interface SearchLabelState {
  labelSearchFilter: string | undefined;
  primaryCategoryFilter: Category[];
  unitsFilter: Unit[];
}

export interface AnnotationsState {
  active: boolean;
  mode: 'Brush' | 'Text';
  brushWidth: number;
  colour: string;
  fontSize: number;
  reset: boolean;
}

export const Layout = () => {
  const { isMobile } = useDeviceType();

  const [areaNavigationWarningState, setAreaNavigationWarningState] = useState<boolean>(false);
  const [sideMenuState, setSideMenuState] = useState<SideMenuState>({
    open: false,
    view: 'Search'
  });
  const [toolboxState, setToolboxState] = useState<ToolboxState>({
    open: false,
    view: 'Help'
  });
  const [searchLabelsState, setSearchLabelsState] = useState<SearchLabelState>({
    labelSearchFilter: undefined,
    primaryCategoryFilter: [],
    unitsFilter: []
  });
  const [navBarMobileOpen, setnavBarMobileOpen] = useState(false);
  const [headerRef, { height: headerHeight }] = useElementSize();
  const [subjectViewState, setSubjectViewState] = useState<SubjectViewState>({
    reset: false,
    labelsOn: true,
    labelsSize: 0.9,
    axesOn: true,
    contextOn: !isMobile,
    contextSize: 260,
    labelsOnHover: true,
    labelsWithNumbers: false
  });

  const [annotationsState, setAnnotationsState] = useState<AnnotationsState>({
    active: false,
    mode: 'Brush',
    brushWidth: 5,
    colour: '#B71C1C',
    fontSize: 2,
    reset: false
  });

  // Layout is a pathless route - params come from child routes.
  // strict: false returns match.params (all accumulated URL params) rather than
  // _strictParams (only this route's own params, which would be empty for /_layout).
  const params = useParams({ strict: false }) as {
    viewerType?: ViewerType;
    subjectSlug?: string;
    areaSlug?: string;
  };
  const { viewerType, subjectSlug, areaSlug } = params;
  const { data: modelConfig } = useSubjectConfig(
    subjectSlug,
    viewerType,
    areaSlug
  );
  const { data: areaConfig } = useAreaConfig(areaSlug);
  const searchParams = useSearch({ strict: false });
  const title = areaConfig ? areaConfig.title : '';
  const dialogTitle = `Please note`;
  const dialogDescription = `Navigating home will clear your currently selected ${areaConfig?.subjectName.singular}. \n\nWould you like to proceed?`;
  const dialogOkText = 'OK';
  const dialogCancelText = 'Cancel';

  const openAreaSlugModal = (e: any) => {
    e.preventDefault();
    if (isMobile) {
      setSideMenuOpen(false);
      setToolboxState({ open: false, view: 'Help' });
    }
    setAreaNavigationWarningState(true);
  };

  const closeAreaSlugModal = (e: any) => {
    e.preventDefault();
    setAreaNavigationWarningState(false);
  };

  const navigate = useNavigate();
  const navigateToAreaLandingPage = (e: any) => {
    e.preventDefault();
    setAreaNavigationWarningState(false);
    if (!areaSlug) return;
    navigate({ to: '/view/$areaSlug', params: { areaSlug } });
    setSideMenuState({ open: true, view: 'Search' });
    setToolboxState({ open: false, view: 'Help' });
    setSearchLabelsState({ ...searchLabelsState, labelSearchFilter: undefined });
  };

  const openSideMenuView = (view: SideMenuViews) => {
    return (e: any) => {
      e.preventDefault();
      if (view === 'Details') {
        // Remove selectedLabel from search params
        const { selectedLabel, ...rest } = searchParams;
        navigate({ search: rest as any, replace: true });
      }
      setnavBarMobileOpen(false);
      if (isMobile) {
        setToolboxOpen(false);
      }
      setSideMenuView(view);
      DXPAnalytics.trackAction({
        name: 'OPEN_SIDE_MENU',
        context: { ...getCommonAnalyticsContext(), context5: view }
      });
    };
  };

  const setSideMenuView = (view: SideMenuViews) => {
    setSideMenuState({ open: true, view: view });
  };

  const setSideMenuOpen = (open: boolean) => {
    setSideMenuState({ ...sideMenuState, open: open });
  };

  const setToolboxView = (view: ToolboxViews) => {
    if (isMobile) {
      setSideMenuOpen(false);
    }
    setToolboxState({ open: true, view: view });
    DXPAnalytics.trackAction({
      name: 'OPEN_TOOLBOX_VIEW',
      context: { ...getCommonAnalyticsContext(), context5: view }
    });
  };

  const setToolboxOpen = (open: boolean) => {
    setToolboxState({ ...toolboxState, open: open });
  };

  const toggleResetView = () => {
    setSubjectViewState({ ...subjectViewState, reset: true });
    DXPAnalytics.trackAction({
      name: 'RESET_VIEW',
      context: getCommonAnalyticsContext()
    });
  };

  const onChangeLabelSearchFilter: ChangeEventHandler<HTMLInputElement> | undefined = (e: any) => {
    setSearchLabelsState({ ...searchLabelsState, labelSearchFilter: e.target.value });
  };

  const getUpdatedLabels = (): LabelConfig[] => {
    return (modelConfig?.labels ?? [])
      .filter((label) =>
        searchLabelsState.labelSearchFilter
          ? label.title.toLowerCase().includes(searchLabelsState.labelSearchFilter.toLowerCase())
          : true
      )
      .filter((label) =>
        searchLabelsState.primaryCategoryFilter.length
          ? searchLabelsState.primaryCategoryFilter.some((sf) => label.primaryCategoryId.id === sf.id)
          : true
      )
      .filter((label) =>
        searchLabelsState.unitsFilter.length
          ? searchLabelsState.unitsFilter.some((uf) => label.unitIds.some((id) => id === uf.id))
          : true
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  useEffect(() => {
    // handle browser back button click to reset the side menu view
    const handlePopState = (e: any) => {
      if (e.target.location.pathname === `/view/${areaSlug}`) {
        navigateToAreaLandingPage(e);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {areaNavigationWarningState && (
        <Modal
          title={dialogTitle}
          description={dialogDescription}
          hideXButton={true}
          okButtonTitle={dialogOkText}
          okAction={navigateToAreaLandingPage}
          cancelButtonTitle={dialogCancelText}
          cancelAction={closeAreaSlugModal}
        />
      )}
      <Header
        title={title}
        openAreaSlugModal={openAreaSlugModal}
        sideMenuState={sideMenuState}
        setToolboxView={setToolboxView}
        toolboxState={toolboxState}
        toggleResetView={toggleResetView}
        navBarMobileOpen={navBarMobileOpen}
        setnavBarMobileOpen={setnavBarMobileOpen}
        openSideMenuView={openSideMenuView}
        areaConfig={areaConfig}
        modelConfigAvailable={!!modelConfig}
        ref={headerRef}
      />
      <SideMenu
        open={sideMenuState.open}
        setOpen={setSideMenuOpen}
        view={sideMenuState.view}
        topOffset={headerHeight}
        subjectViewState={subjectViewState}
        setSubjectViewState={setSubjectViewState}
        searchLabelsState={searchLabelsState}
        setSearchLabelsState={setSearchLabelsState}
        onChangeLabelSearchFilter={onChangeLabelSearchFilter}
        getUpdatedLabels={getUpdatedLabels}
      />
      <Toolbox
        open={toolboxState.open}
        setOpen={setToolboxOpen}
        view={toolboxState.view}
        topOffset={headerHeight}
        subjectViewState={subjectViewState}
        setSubjectViewState={setSubjectViewState}
        annotationsState={annotationsState}
        setAnnotationsState={setAnnotationsState}
      />

      {isMobile && (
        <NavBarMenuMobile
          headerHeight={headerHeight}
          navBarMobileOpen={navBarMobileOpen}
          setnavBarMobileOpen={setnavBarMobileOpen}
          sideMenuState={sideMenuState}
          openSideMenuView={openSideMenuView}
          areaConfig={areaConfig}
          modelConfigAvailable={!!modelConfig}
        />
      )}

      <main className="flex-1 layout">
        <LayoutContext.Provider
          value={{
            sideMenuState,
            setSideMenuState,
            subjectViewState,
            setSubjectViewState,
            toggleResetView,
            setToolboxView,
            toolboxState,
            searchLabelsState,
            getUpdatedLabels,
            headerHeight,
            annotationsState,
            setAnnotationsState
          }}
        >
          <Outlet />
        </LayoutContext.Provider>
      </main>
    </div>
  );
};
